import React, { useState, useEffect, useContext } from 'react';
import { Container, Col, Alert, Spinner, Card } from 'react-bootstrap';
import axios from 'axios';
import { AuthContext } from '../login/Auth';
import NavigationBar from '../components/nav';
import UserTable from '../components/table';
import app from '../login/firebase.js';

const Home = () => {
  const [trialData, setTrialData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { currentUser, allowEdit } = useContext(AuthContext);

  useEffect(() => {
    fetchTrialData();
  }, []);

  const fetchTrialData = async () => {
    setIsLoading(true);
    try {
      const idToken = await app.auth().currentUser.getIdToken(true);

      const response = await axios.get(process.env.REACT_APP_summuryURL, {
        headers: {
          'Content-Type': 'application/json',
          'authorization': idToken,
          'Cache-Control': 'no-cache'
        }
      });

      setTrialData(response.data.data);
      setIsLoading(false);
      setError(null);
    } catch (err) {
      setIsLoading(false);

      if (err.response) {
        setError(err.response.data.message || 'An error occurred while fetching data');
      } else if (err.request) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again later.');
      }
    }
  };

  // Check for success message from redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const message = params.get('message');

    if (success === 'true' && message) {
      setSuccessMessage(decodeURIComponent(message));

      // Clear message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleRefresh = () => {
    fetchTrialData();
  };

  return (
    <div className="home-page">
      <NavigationBar />

      <Container className="mt-4">
        <Card className="shadow-sm mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center bg-light">
            <h4 className="mb-0">Parkinson Registry</h4>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  {/*<span className="ms-2">Loading...</span>*/}
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Refresh
                </>
              )}
            </button>
          </Card.Header>

          <Card.Body>
            {successMessage && (
              <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
                {successMessage}
              </Alert>
            )}

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <div>
                    <strong>Error:</strong> {error}
                    <div className="mt-2">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={handleRefresh}
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              </Alert>
            )}

            {isLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                  {/*<span className="visually-hidden">Loading...</span>*/}
                </Spinner>
                <p className="mt-3 text-muted">Loading trial data...</p>
              </div>
            ) : trialData ? (
              <UserTable
                data={trialData}
                allowEdit={allowEdit}
                onRefresh={handleRefresh}
              />
            ) : !error ? (
              <div className="text-center py-5">
                <i className="bi bi-clipboard-x fs-1 text-muted"></i>
                <p className="mt-3">No trial data available</p>
              </div>
            ) : null}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default Home;